// component render when create activity -> select activity -> customize activity (here)
import {
  Box,
  Button,
  Checkbox,
  Divider,
  TextField,
  Typography,
  Grid,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Fade from '@mui/material/Fade';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { v4 as uuidv4 } from 'uuid';
import AddMediaButton from './AddMediaTray';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  ActivityTemplateType,
  AssetType,
  IActivityStep,
  checkActivityExistence,
  resetCheckActivityExistenceStatus,
  setDraftActivity,
} from '../../../redux/activity/activitySlice';
import ErrorModal from '../../ErrorModal/ErrorModal';
import { slimScrollbarStyles } from '../../../utils/styles';
import StyledTextField from '../../Common/StyledTextField';
import StyledBackButton from '../../Common/StyledBackButton';
import StyledButton from '../../Common/StyledButton';
import StyledDropdown from '../../Common/StyledDropdown';
import {
  ActivityTypeEnum,
  AdvanceActivityStepType,
  advanceActivityStepTypes,
} from '../../../types/activity';
import { clearFolder } from '../../../redux/activityLibrary/activityLibrarySlice';

interface IStep {
  id?: number | string;
  text: string;
  description: string;
  stepOrder: number;
  // asset file name
  asset: string | null;
  // enum for asset type
  assetType: 'IMAGE' | 'AUDIO' | 'DOCUMENT' | 'VIDEO' | 'TEXT';
  // image url for preview ( local url) can create url from asset file
  // new URL(file).createObjectURL(file)
  image: string | null;
  // content type of asset
  contentType: string | null;
  //
  assetFile: File | null;

  isAdvanceStep?: boolean;
  advanceStepType?: AdvanceActivityStepType | '';
  advanceStepData?: string[];
}

const StyledDashedButton = styled(Button)({
  border: '2px dashed var(--Accent-Moonstone-Blue, #7CAEBE)',
  minWidth: '160px',
  height: '48px',
  justifyContent: 'center',
  padding: '12px',
  '&:hover': {
    border: '2px dashed var(--Accent-Moonstone-Blue, #7CAEBE)',
  },
});

const StyledImageDeleteButton = styled(Box)({
  position: 'absolute',
  top: -17,
  left: 133,
  background: 'red',
  color: 'white',
  width: '35px',
  height: '35px',
  // minWidth: 'initial',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
});

interface IStyledTextFieldProps {
  width?: string;
}

const CommonTextField = styled(TextField)<IStyledTextFieldProps>(({ theme, error = false }) => ({
  height: '60px',
  '& .MuiInputBase-root': {
    backgroundColor: error ? '#E161621A' : '#FFFFFF',
    borderRadius: '10px',
  },
  '& .MuiInputBase-input': {
    ...theme.typography.h6,
    color: error ? '#E16162' : theme.palette.primary.main,
    '&:-webkit-autofill': {
      '-webkit-text-fill-color': theme.palette.primary.main,
      '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.primary.light} inset`,
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
    '&:hover fieldset': {
      border: '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid var(--Accent-Moonstone-Blue, #7CAEBE)',
    },
  },
  '& ::placeholder': {
    opacity: 0.2,
  },
}));

interface ICustomizeActivityContainerProps {
  handleNext: () => void;
  closeModel: () => void;
  handleBack: () => void;
}

const initialStep: IStep = {
  text: '',
  description: '',
  stepOrder: 0,
  asset: '',
  assetType: 'TEXT',
  image: null,
  contentType: null,
  assetFile: null,
  isAdvanceStep: false,
  advanceStepType: '',
  advanceStepData: [],
};

function CustomizeActivityContainer({
  handleNext,
  closeModel,
  handleBack,
}: Readonly<ICustomizeActivityContainerProps>) {
  const maxSizeImage = process.env.MAX_SIZE_IMAGE ?? 10 * 1024 * 1024;
  const maxSizeAudio = process.env.MAX_SIZE_AUDIO ?? 50 * 1024 * 1024;
  const maxSizeVideo = process.env.MAX_SIZE_VIDEO ?? 50 * 1024 * 1024;

  const draftedActivity = useAppSelector((state) => state.activity.draftActivity);
  const activityExistenceStatus = useAppSelector((state) => state.activity.activityExistenceStatus);
  const [steps, setSteps] = useState<IStep[]>([{ ...initialStep }]);
  const selectedClient = useAppSelector((state) => state.client.selectedClient);
  const [title, setTitle] = useState('');
  const [objective, setObjective] = useState('');
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [enableAdvancedTrackingOptions, setEnableAdvancedTrackingOptions] = useState(false);
  const [isAllValid, setIsAllValid] = useState(false);
  const [frequency, setFrequency] = useState('');
  const [situation, setSituation] = useState<string>('');
  const [isSaveToActivityLibrary, setIsSaveToActivityLibrary] = useState(false);
  let normalStepCounter = 0;
  let advancedStepCounter = 0;

  const showErrorModal = (message: string) => {
    setErrorMessage(message);
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorMessage('');
  };

  useEffect(() => {
    if (draftedActivity) {
      setTitle(draftedActivity.name);
      setObjective(draftedActivity.objective);
      setFrequency(draftedActivity.frequency as ActivityTypeEnum);
      setSituation(draftedActivity.situation as string);
      let transformedSteps: IStep[] = [];
      let hasAdvanceStep: boolean = false;
      if (draftedActivity.ActivityStep) {
        transformedSteps = draftedActivity.ActivityStep.map((step) => ({
          text: step.text,
          description: '',
          stepOrder: step.stepOrder,
          asset: step.asset || '',
          assetType: step.assetType ?? 'TEXT',
          image: step.assetFile ? URL.createObjectURL(step.assetFile) : '',
          contentType: step.contentType ?? '',
          assetFile: step.assetFile || null,
          isAdvanceStep: step.isAdvanceStep || false,
          advanceStepType:
            step.advanceStepType !== '' ? step.advanceStepType : AdvanceActivityStepType.ANY,
          advanceStepData: step.advanceStepData || [],
        }));
        hasAdvanceStep = transformedSteps.some((step) => step.isAdvanceStep);
      }
      transformedSteps.sort((a, b) => a.stepOrder - b.stepOrder);
      if (transformedSteps.length !== 0) {
        setSteps(transformedSteps);
      }
      setEnableAdvancedTrackingOptions(hasAdvanceStep);
    }
  }, [draftedActivity]);

  const addStepHandler = (isAdvanceStep: boolean) => {
    if (isAdvanceStep) {
      setSteps((prv) => [...prv, { id: uuidv4(), ...initialStep, isAdvanceStep }]);
    } else {
      setSteps((prv) => [...prv, { id: uuidv4(), ...initialStep }]);
    }
  };

  const addOptionHandler = (index: number) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) => {
        if (i === index) {
          const updatedAdvanceStepData = step.advanceStepData
            ? [...step.advanceStepData, '']
            : [''];
          return {
            ...step,
            advanceStepData: updatedAdvanceStepData,
          };
        }
        return step;
      })
    );
  };

  const removeStepHandler = (index: number) => {
    setSteps((prv) => prv.filter((_, i) => i !== index));
  };

  const removeOptionsHandler = (index: number, optionIndex: number) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) => {
        if (i === index && step.advanceStepData) {
          const filteredAdvanceStepData = step.advanceStepData.filter((_, j) => j !== optionIndex);
          return {
            ...step,
            advanceStepData: filteredAdvanceStepData,
          };
        }
        return step;
      })
    );
  };

  const handleStepChange = (index: number, value: string) => {
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[index].text = value;
      return newSteps;
    });
  };

  const handleInputChange = (index: number, value: string) => {
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[index].text = value;
      newSteps[index].isAdvanceStep = true;
      return newSteps;
    });
  };

  const handleMultipleChoiceInputChange = (index: number, value: string, optionIndex: number) => {
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      const advanceStepData = [...(newSteps[index].advanceStepData || [])]; // Create a copy of the nested array
      advanceStepData[optionIndex] = value; // Update the value in the copied array
      newSteps[index] = { ...newSteps[index], advanceStepData }; // Update the step with the new nested array
      return newSteps;
    });
  };

  const handleTypeChange = (index: number, value: AdvanceActivityStepType) => {
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[index].advanceStepType = value;
      return newSteps;
    });
  };

  const handleMediaChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    let maxSize;
    if (file.type.startsWith('image/')) {
      maxSize = maxSizeImage;
    } else if (file.type.startsWith('audio/')) {
      maxSize = maxSizeAudio;
    } else if (file.type.startsWith('video/')) {
      maxSize = maxSizeVideo;
    } else {
      showErrorModal('Invalid file type');
      return;
    }
    if (file.size > maxSize) {
      showErrorModal(
        t('error.fileUpload.fileSizeTooLarge', {
          maxSize: +(Math.floor((maxSize / 1024 / 1024) * 10) / 10),
        })
      );
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[index].image = imageUrl;
      newSteps[index].assetFile = file;
      newSteps[index].asset = file.name;
      newSteps[index].contentType = file.type;

      let assetType: 'IMAGE' | 'AUDIO' | 'DOCUMENT' | 'VIDEO';
      if (file.type.startsWith('image/')) {
        assetType = 'IMAGE';
      } else if (file.type.startsWith('video/')) {
        assetType = 'VIDEO';
      } else {
        assetType = 'AUDIO';
      }
      newSteps[index].assetType = assetType;
      return newSteps;
    });
  };

  const handleAssignToClient = () => {
    dispatch(
      setDraftActivity({
        name: title,
        objective,
        type: 'ACTIVITY',
        frequency: frequency as ActivityTypeEnum,
        situation,
        isAddToLibrary: isSaveToActivityLibrary,
        ActivityStep: steps
          .map((step, index) => {
            if (enableAdvancedTrackingOptions) {
              return {
                text: step.text,
                description: '',
                stepOrder: index + 1 || 1,
                asset: step.asset ?? '',
                assetType: AssetType[step.assetType],
                contentType: step.contentType ?? '',
                assetFile: step.assetFile || null,
                isAdvanceStep: step.isAdvanceStep || false,
                advanceStepType:
                  step.advanceStepType !== '' ? step.advanceStepType : AdvanceActivityStepType.ANY,
                advanceStepData: step.advanceStepData || [],
              };
            }
            if (!step.isAdvanceStep) {
              return {
                text: step.text,
                description: '',
                stepOrder: index + 1 || 1,
                asset: step.asset ?? '',
                assetType: AssetType[step.assetType],
                contentType: step.contentType ?? '',
                assetFile: step.assetFile || null,
                isAdvanceStep: false,
              };
            }
            return null;
          })
          .filter((step) => step !== null) as IActivityStep[],
      })
    );
    handleNext();
  };

  const handleImageRemove = (index: number) => {
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[index].image = null;
      newSteps[index].assetType = 'TEXT';
      newSteps[index].assetFile = null;
      newSteps[index].asset = null;
      newSteps[index].contentType = null;
      return newSteps;
    });
  };

  const stepsContainDuplicates = (allSteps: IStep[]): boolean => {
    const textSet = new Set<string>();
    return allSteps.some((step) => {
      if (textSet.has(step.text)) {
        return true;
      }
      textSet.add(step.text);
      return false;
    });
  };

  const checkDuplicates = (step: IStep, index: number): boolean =>
    steps.slice(0, index).some((step1) => step.text !== '' && step1.text === step.text);
  const hasAdvancedStepData = (step: IStep) => {
    if (step.advanceStepType === AdvanceActivityStepType.SELECTION) {
      return (
        step.advanceStepData?.length === 0 ||
        step.advanceStepData?.some((data) => data.trim() === '')
      );
    }
    return !step.advanceStepType || step.advanceStepType.trim() === '';
  };
  useEffect(() => {
    let isButtonDisabled = false;

    if (title.trim() === '') {
      isButtonDisabled = true;
    } else if (objective.trim() === '') {
      isButtonDisabled = true;
    } else if (steps.some((step) => step.text.trim() === '')) {
      isButtonDisabled = true;
    } else if (activityExistenceStatus === false) {
      isButtonDisabled = true;
    } else if (enableAdvancedTrackingOptions) {
      if (steps.filter((step) => step.isAdvanceStep).some(hasAdvancedStepData)) {
        isButtonDisabled = true;
      } else if (stepsContainDuplicates(steps)) {
        isButtonDisabled = true;
      } else if (advancedStepCounter === 0) {
        isButtonDisabled = true;
      }
    }
    setIsAllValid(!isButtonDisabled);
  }, [title, objective, steps, activityExistenceStatus, enableAdvancedTrackingOptions]);

  const checkActivityExistenceDebounce = debounce((activityTitle: string) => {
    dispatch(checkActivityExistence(activityTitle));
  }, 1000);

  useEffect(() => {
    checkActivityExistenceDebounce(title);

    return () => {
      checkActivityExistenceDebounce.cancel();
      dispatch(resetCheckActivityExistenceStatus());
    };
  }, [title]);

  const handleEnableAdvancedTrackingOptions = () => {
    setEnableAdvancedTrackingOptions(!enableAdvancedTrackingOptions);
  };

  const handleSavetoActivityLibrary = () => {
    setIsSaveToActivityLibrary(!isSaveToActivityLibrary);
  };

  useEffect(
    () => () => {
      dispatch(clearFolder());
    },
    []
  );

  return (
    <Grid
      boxSizing="border-box"
      flexDirection="column"
      display="flex"
      justifyContent="space-between"
      width="100%"
      height="95vh"
      minWidth="70vw"
      p={{ xs: '16px', lg: '32px' }}
      gap={{ xs: '16px', lg: '32px' }}
      overflow="auto"
      sx={{
        ...slimScrollbarStyles,
      }}
    >
      {/* Header */}
      <Grid>
        <Grid display="flex" justifyContent="space-between">
          <Typography variant="h4" color="primary.main">
            {t('activity.createActivity.createNewActivity')}
          </Typography>
          <img
            src="/assets/icons/close-circle-large.svg"
            alt="close-icon"
            role="presentation"
            style={{
              cursor: 'pointer',
            }}
            onClick={closeModel}
          />
        </Grid>
        <Divider sx={{ pt: '24px' }} />
      </Grid>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="bodyBold" color="primary.main">
          {t('activity.createActivity.buildYourActivity')}
        </Typography>
      </Box>

      <Box
        flex="1"
        sx={{
          background:
            'linear-gradient(0deg, rgba(255, 221, 200, 0.25) 0%, rgba(255, 221, 200, 0.25) 100%), #FFF',
          border: '1px #FFDDC8 solid',
          borderRadius: '10px',
        }}
      >
        <Box
          overflow="auto"
          // p="36px 20%"
          p={{ xs: '16px', lg: '32px 20%' }}
          // height="50vh"
          sx={{
            ...slimScrollbarStyles,
          }}
        >
          {/* second row  activity selector */}
          <Box display="flex" flexDirection="column" mx="auto">
            <Box display="flex" flexDirection="column" gap="4px">
              <Typography variant="tinyTexts" color="primary.main">
                {t('activity.createActivity.title')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <CommonTextField
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
                error={activityExistenceStatus === false}
                value={title}
              />
              <Typography variant="bodyRegular" color="error.main">
                {activityExistenceStatus === false && t('activity.createActivity.activityExists')}
              </Typography>
            </Box>
            <Box width="100%" display="flex" flexDirection="column" mt="12px" gap="4px">
              <Typography variant="tinyTexts" color="primary.main">
                {t('activity.createActivity.objective')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <StyledTextField
                onChange={(e) => {
                  setObjective(e.target.value);
                }}
                // multiline
                // rows={2}
                value={objective}
              />
            </Box>
            <Box
              height="100%"
              boxSizing="border-box"
              gap="32px"
              display="flex"
              flexDirection="column"
              flexGrow={1}
              pt="32px"
              sx={{
                ...slimScrollbarStyles,
              }}
            >
              {steps.map((step, index) => {
                if (!step.isAdvanceStep) {
                  normalStepCounter += 1;
                  return (
                    <Box display="flex" width="100%" flexDirection="column" key={step.id}>
                      <Typography variant="meta" color="primary.main">
                        {t('activity.createActivity.step')} {normalStepCounter}
                      </Typography>
                      <Box position="relative">
                        {(step.image || step.asset) && step.assetType === 'IMAGE' && (
                          <>
                            <Fade
                              in={!!step.image || !!step.asset}
                              timeout={{
                                enter: 1000,
                                exit: 300,
                              }}
                              onExited={() => {
                                // Update the state to remove the image from the array
                                setSteps((prevSteps) => {
                                  const newSteps = [...prevSteps];
                                  newSteps[index].image = '';
                                  return newSteps;
                                });
                              }}
                            >
                              <img
                                src={
                                  step.image
                                    ? step.image
                                    : `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${step.asset}`
                                }
                                alt={`Step ${index + 1}`}
                                style={{
                                  width: '150px',
                                  height: '150px',
                                }}
                              />
                            </Fade>

                            <StyledImageDeleteButton onClick={() => handleImageRemove(index)}>
                              <img
                                src="/assets/icons/delete-bin.svg"
                                alt="close-icon"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                }}
                              />
                            </StyledImageDeleteButton>
                          </>
                        )}
                        {(step.image || step.asset) && step.assetType === 'VIDEO' && (
                          <>
                            <ReactPlayer
                              url={
                                step.image
                                  ? step.image
                                  : `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${step.asset}`
                              }
                              width="150px"
                              height="150px"
                              controls
                              data-testid="demo-video-player"
                            />
                            <StyledImageDeleteButton onClick={() => handleImageRemove(index)}>
                              <img
                                src="/assets/icons/delete-bin.svg"
                                alt="close-icon"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                }}
                              />
                            </StyledImageDeleteButton>
                          </>
                        )}
                        {(step.image || step.asset) && step.assetType === 'AUDIO' && (
                          <>
                            <audio
                              src={
                                step.image
                                  ? step.image
                                  : `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${step.asset}`
                              }
                              controls
                              style={{ width: '150px' }}
                            >
                              <track kind="captions" />
                            </audio>
                            <StyledImageDeleteButton onClick={() => handleImageRemove(index)}>
                              <img
                                src="/assets/icons/delete-bin.svg"
                                alt="close-icon"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                }}
                              />
                            </StyledImageDeleteButton>
                          </>
                        )}
                      </Box>
                      <Box
                        display="flex"
                        gap="24px"
                        width="100%"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box display="flex" flexDirection="column" width="100%">
                          <StyledTextField
                            onChange={(e) => handleStepChange(index, e.target.value)}
                            value={step.text}
                            // multiline
                            // rows={1}
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <Box>
                                  <AddMediaButton
                                    handleFileSelect={handleMediaChange}
                                    fileInputKey={index}
                                  />
                                </Box>
                              ),
                            }}
                          />
                          <Typography variant="bodyRegular" color="error.main">
                            {checkDuplicates(step, index) &&
                              t('activity.createActivity.stepExists')}
                          </Typography>
                        </Box>

                        <Box onClick={() => removeStepHandler(index)}>
                          <img
                            src="/assets/icons/step-close-circle.svg"
                            alt="close-icon"
                            style={{
                              cursor: 'pointer',
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                }
                return null;
              })}
            </Box>
          </Box>
        </Box>
        <Box>
          <Box
            p={{
              xs: '16px',
              lg: '0 20%',
            }}
          >
            {/* add step button */}
            <StyledDashedButton
              variant="outlined"
              color="primary"
              onClick={() => addStepHandler(false)}
              fullWidth
            >
              <img src="/assets/icons/plus-button.svg" alt="plus-button" />
            </StyledDashedButton>

            <Box
              pt={4}
              display="flex"
              alignItems="center"
              pb={!enableAdvancedTrackingOptions ? 4 : 0}
              gap="5px"
            >
              <Checkbox
                sx={{
                  padding: 0,
                  margin: 0,
                  color: '#6CA3AC',
                  '&.Mui-checked': {
                    color: '#6CA3AC',
                  },
                }}
                onChange={handleEnableAdvancedTrackingOptions}
                checked={enableAdvancedTrackingOptions}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              <Typography variant="bodyRegular" color="primary">
                {t('activity.createActivity.enableAdvancedTracking')}
              </Typography>
              <Tooltip
                title={t('activity.createActivity.tooltip')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'primary.main',
                      borderRadius: '5px',
                      padding: '8px 12px',
                    },
                  },
                  arrow: {
                    sx: {
                      '&:before': {
                        bgcolor: 'primary.main',
                      },
                    },
                  },
                }}
              >
                <img src="/assets/icons/info-circle.svg" alt="i-icon" />
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {enableAdvancedTrackingOptions && (
          <>
            <Box
              overflow="auto"
              // p="36px 20%"
              p={{ xs: '16px', lg: '0 20% 32px 20%' }}
              // height="50vh"
              sx={{
                ...slimScrollbarStyles,
              }}
            >
              {/* second row  activity selector */}
              <Box display="flex" flexDirection="column" mx="auto">
                <Box
                  height="100%"
                  boxSizing="border-box"
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                  pt="32px"
                  sx={{
                    ...slimScrollbarStyles,
                  }}
                >
                  {steps.map((step, index) => {
                    if (step.isAdvanceStep) {
                      advancedStepCounter += 1;
                      return (
                        <>
                          {advancedStepCounter === 1 && (
                            <Grid container>
                              <Grid item xs={6}>
                                <Typography variant="meta" color="primary.main">
                                  {t('activity.createActivity.input')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="meta" color="primary.main">
                                  {t('activity.createActivity.type')}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                          <Grid
                            display="flex"
                            flexDirection="row"
                            key={step.id}
                            gap="20px"
                            // alignItems="center"
                            mt="12px"
                          >
                            <Grid item xs={6} display="flex" flexDirection="column">
                              <StyledTextField
                                onChange={(e) => handleInputChange(index, e.target.value)}
                                value={step.text}
                                // multiline
                                // rows={1}
                                fullWidth
                                placeholder={t('activity.createActivity.placeholder')}
                              />
                              <Typography variant="bodyRegular" color="error.main">
                                {checkDuplicates(step, index) &&
                                  t('activity.createActivity.stepExists')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <StyledDropdown
                                name="advanceActivityStepTypes"
                                options={advanceActivityStepTypes}
                                value={step.advanceStepType as AdvanceActivityStepType}
                                onChange={(e) =>
                                  handleTypeChange(index, e.target.value as AdvanceActivityStepType)
                                }
                                isTransalationProvided
                              />
                              {step.advanceStepType === AdvanceActivityStepType.SELECTION &&
                                step.advanceStepData?.map((option, optionIndex) => (
                                  <Grid
                                    display="flex"
                                    flexDirection="row"
                                    gap="10px"
                                    alignItems="center"
                                  >
                                    <StyledTextField
                                      onChange={(e) =>
                                        handleMultipleChoiceInputChange(
                                          index,
                                          e.target.value,
                                          optionIndex
                                        )
                                      }
                                      value={option}
                                      // multiline
                                      // rows={1}
                                      fullWidth
                                      style={{ marginTop: '12px' }}
                                      placeholder={t('activity.createActivity.placeholder')}
                                    />
                                    <Grid onClick={() => removeOptionsHandler(index, optionIndex)}>
                                      <img
                                        src="/assets/icons/step-close-circle.svg"
                                        alt="close-icon"
                                        style={{
                                          cursor: 'pointer',
                                          marginTop: '10px',
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                ))}
                              {step.advanceStepType === AdvanceActivityStepType.SELECTION && (
                                <StyledDashedButton
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => addOptionHandler(index)}
                                  fullWidth
                                  sx={{ mt: '12px' }}
                                >
                                  <img src="/assets/icons/plus-button.svg" alt="plus-button" />
                                </StyledDashedButton>
                              )}
                            </Grid>
                            <Grid onClick={() => removeStepHandler(index)}>
                              <img
                                src="/assets/icons/step-close-circle.svg"
                                alt="close-icon"
                                style={{
                                  cursor: 'pointer',
                                  marginTop: '14px',
                                }}
                              />
                            </Grid>
                          </Grid>
                        </>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Box>
            </Box>
            <Box>
              <Box
                p={{
                  xs: '16px',
                  lg: '0 20% 32px 20%',
                }}
              >
                {/* add step button */}
                <StyledDashedButton
                  variant="outlined"
                  color="primary"
                  onClick={() => addStepHandler(true)}
                  fullWidth
                >
                  <img src="/assets/icons/plus-button.svg" alt="plus-button" />
                </StyledDashedButton>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* third row */}
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <StyledBackButton variant="outlined" onClick={handleBack}>
          <span>{t('common.back')}</span>
        </StyledBackButton>

        <Box display="flex" gap="20px">
          {draftedActivity?.type !== ActivityTemplateType.ACTIVITY_LIBRARY && (
            <Box display="flex" alignItems="center" justifyContent="center">
              <Checkbox
                sx={{
                  padding: 0,
                  margin: 0,
                  color: '#6CA3AC',
                  '&.Mui-checked': {
                    color: '#6CA3AC',
                  },
                }}
                onChange={handleSavetoActivityLibrary}
                checked={isSaveToActivityLibrary}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              <Typography variant="bodyRegular" color="primary">
                {t('activity.createActivity.saveToActivityLibrary')}
              </Typography>
            </Box>
          )}

          <StyledButton
            disabled={!isAllValid || activityExistenceStatus === null}
            onClick={handleAssignToClient}
            loadingText={t('activity.assignActivity.creatingActivity')}
          >
            {`${t('activity.createActivity.assignTo')} ${selectedClient?.firstName}`}
          </StyledButton>
        </Box>
      </Box>
      <ErrorModal isOpen={isErrorModalOpen} message={errorMessage} onClose={closeErrorModal} />
    </Grid>
  );
}

export default CustomizeActivityContainer;
